import Decimal from 'decimal.js';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { getTankContentInBerthCall, getTankContentsInPortCall, updateManifest } from '../manifest/cargoManifestUtils';
import { updateUnstowOperationsForCargo } from './autoUnstowUtils';
import { calculateWeight, getCorrectedLitreWeight } from './volumeUtils';
import { getCargoTemperature } from './cargoTemperatureUtils';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { createDeleteStowagePlanChange, createStowagePlanChange } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { TankContent } from 'src/types/ViewModel/Stowage/Manifest/TankContent';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { Stowage } from 'src/types/ViewModel/Stowage/Stowage';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { getTankContentForCondition } from '../manifest/tankPlanConditionUtils';
import { TankPlanCondition } from 'src/enums/stowage/tankPlanCondition';

const logLevelTankUtils: boolean = false;

const getLoadingOperationsFromTankContents = (tankContents: TankContent[]): RotationCargoOperation[] => {
    const loadingOperations = tankContents
        .filter((content) => content && content.cargoContents)
        .flatMap((content) => content.cargoContents)
        .filter((cargoContent) => cargoContent?.cargoOperation?.operationType === 'Loading')
        .map((cargoContent) => cargoContent.cargoOperation);

    return loadingOperations;
};

const getBerthCallInWhichTanksHaveLoadingOperation = (
    stowagePlan: StowagePlan,
    tanks: Tank[],
    portCall: RotationPortCall
): RotationBerthCall | undefined => {
    const tankContents = tanks.map((tank) => getTankContentsInPortCall(stowagePlan, portCall, tank));
    if (tankContents.length === 0) {
        return undefined; // No tank contents found for the port call
    }

    const loadingOperations = tankContents.map((tc) => getLoadingOperationsFromTankContents(tc));

    // Get distinct loading operations
    const distinctLoadingOperationGuids = new Set(
        loadingOperations.flatMap((operations) => operations.map((op) => op.cargoOperationGuid?.toString()).filter((guid) => guid !== undefined))
    );

    // If there are multiple distinct loading operations in selected tanks, return undefined
    if (distinctLoadingOperationGuids.size > 1) {
        return undefined;
    }

    // Return the berth call from the only loading operation
    return loadingOperations[0][0]?.berthCall;
};

const otherContentUnloadedThisBerthCall = (stowagePlan: StowagePlan, content: TankContent | null, levelingCargo: RotationCargoOperation): boolean => {
    if (!content) {
        return false;
    }

    const tankContentsAtDeparture = getTankContentForCondition(
        stowagePlan,
        content.portCall,
        content.berthCall,
        TankPlanCondition.Departure,
        content.tank
    );

    // Tank is considered empty if it has no cargo contents OR all cargo contents have zero/negative weight
    return (
        tankContentsAtDeparture.cargoContents?.length === 0 ||
        tankContentsAtDeparture.cargoContents
            ?.filter((c) => !c?.cargoOperation?.cargoOperationGuid || c.cargoOperation.cargoOperationGuid !== levelingCargo.cargoOperationGuid)
            .every((c) => c.weight.lte(0))
    );
};

export const canLevelTanks = (
    stowagePlan: StowagePlan,
    portCall: RotationPortCall,
    selectedBerthCall: RotationBerthCall | undefined,
    tanks: Tank[]
): boolean => {
    let berthCall = selectedBerthCall;
    if (!selectedBerthCall) {
        berthCall = getBerthCallInWhichTanksHaveLoadingOperation(stowagePlan, tanks, portCall);
        if (berthCall === undefined) {
            return false; // No valid berth call found with loading operations in the selected tanks
        }
    }

    const hasValidInput = berthCall && tanks?.length > 1;
    if (!hasValidInput) {
        return false;
    }

    const tankContents = tanks.map((tank) => getTankContentInBerthCall(stowagePlan, berthCall, tank));

    const loadingOperations = getLoadingOperationsFromTankContents(tankContents);
    const distinctLoadingOperationGuids = new Set(
        loadingOperations.map((operation) => operation.cargoOperationGuid?.toString()).filter((guid) => guid !== undefined)
    );

    if (distinctLoadingOperationGuids.size > 1) {
        return false; // Cannot level tanks with multiple distinct loading operations
    }

    const stowageItems = stowagePlan.stowageItems.filter(
        (item) =>
            tanks.map((t) => t.tankId).includes(item.tankId) &&
            item.cargoOperation.berthCall.berthCallGuid === berthCall.berthCallGuid &&
            item.cargoOperation.operationType === 'Loading'
    );

    const referenceCargo = loadingOperations[0]?.cargo;
    const referenceCargoOperation = loadingOperations[0];

    if (!referenceCargo || !referenceCargoOperation || !referenceCargo.cargoGuid) {
        return false;
    }

    // Check if any selected tank has stowage items that don't belong to this berth call
    // This would indicate multiple loading berths for the same cargo in selection, which should not be leveled together
    const tanksWithStowageInOtherBerthCalls = tanks.some((tank) => {
        const tankStowageItems = stowagePlan.stowageItems.filter(
            (item) =>
                item.cargoOperation.cargo.cargoGuid === referenceCargo.cargoGuid &&
                item.tankId === tank.tankId &&
                item.cargoOperation.portCallGuid === portCall.portCallGuid
        );
        return tankStowageItems.some((item) => item.cargoOperation.berthCall.berthCallGuid !== berthCall.berthCallGuid);
    });

    if (tanksWithStowageInOtherBerthCalls) {
        return false; // Cannot level tanks that have operations in different berth calls
    }

    // Allow leveling if:
    // 1. We have at least one tank with cargo content in this berth call
    // 2. All tanks with content have the same loading operation (same cargoOperationGuid)
    // 3. We have at least one stowage item in this berth call
    return Boolean(
        stowagePlan &&
            tankContents.length > 0 &&
            referenceCargo &&
            referenceCargoOperation &&
            stowageItems.length > 0 &&
            tankContents.every(
                (content) =>
                    content === null || // Empty tank (no content in this berth call) - compatible
                    (content?.cargoContents?.length === 1 &&
                        content.cargoContents[0].cargoOperation?.cargoOperationGuid === referenceCargoOperation.cargoOperationGuid &&
                        content.cargoContents[0].cargoOperation?.operationType === 'Loading') || // Same loading operation
                    otherContentUnloadedThisBerthCall(stowagePlan, content, referenceCargoOperation) // All other content in this tank is unloaded in this berth call
            )
    );
};

const resetUnstowUnloadingOperationsForCargo = (stowagePlan: StowagePlan, cargo: RotationCargo, tankIds: number[]): StowagePlanChange[] => {
    const stowagePlanChanges: StowagePlanChange[] = [];

    const unloadingStowageItems = stowagePlan.stowageItems.filter(
        (s) => tankIds.includes(s.tankId) && s.cargoOperation.operationType === 'Unloading' && s.cargoOperation.cargoGuid === cargo.cargoGuid
    );
    unloadingStowageItems.forEach((s) => {
        stowagePlanChanges.push(createDeleteStowagePlanChange(stowagePlan.stowagePlanGuid, s.stowageGuid, StowageChangeDataType.StowageItem));
    });

    stowagePlan.stowageItems = stowagePlan.stowageItems.filter(
        (item) => !unloadingStowageItems.some((unloadItem) => unloadItem.stowageGuid === item.stowageGuid)
    );

    return stowagePlanChanges;
};

export const levelTanks = (
    stowagePlan: StowagePlan,
    portCall: RotationPortCall,
    selectedBerthCall: RotationBerthCall,
    tanks: Tank[],
    vessel: Vessel
): StowagePlanChange[] => {
    let berthCall = selectedBerthCall;
    // If no berth call is provided, find the one with loading operations in the selected tanks
    if (!berthCall) {
        berthCall = getBerthCallInWhichTanksHaveLoadingOperation(stowagePlan, tanks, portCall);
    }
    let stowagePlanChanges: StowagePlanChange[] = [];

    const workStowagePlan = {
        ...stowagePlan,
        stowageItems: [...stowagePlan.stowageItems],
    };

    const tankIds = tanks.map((t) => t.tankId);
    let cargo: RotationCargo | undefined;
    const tankContents = tanks.map((tank) => getTankContentsInPortCall(stowagePlan, portCall, tank));
    if (tankContents.length === 0) {
        return stowagePlanChanges; // No tank contents found for the port call
    }

    const loadingOperations = tankContents.map((tc) => getLoadingOperationsFromTankContents(tc));

    if (loadingOperations.some((ops) => ops?.length > 0)) {
        cargo = loadingOperations.filter((ops) => ops.length > 0)[0][0]?.cargo;
    } else {
        // No cargo found in any selected tank - cannot proceed
        return stowagePlanChanges;
    }

    const resetUnstowStowagePlanChanges = resetUnstowUnloadingOperationsForCargo(workStowagePlan, cargo, tankIds);
    stowagePlanChanges = stowagePlanChanges.concat(resetUnstowStowagePlanChanges);

    const existingStowageItems = workStowagePlan.stowageItems.filter(
        (s) =>
            tankIds.includes(s.tankId) &&
            s.cargoOperation.berthCall.berthCallGuid === berthCall.berthCallGuid &&
            s.cargoOperation.operationType === 'Loading' &&
            s.cargoOperation.cargoGuid === cargo.cargoGuid
    );

    // Find empty tanks that don't have existing stowage items but are in the selected tanks
    const tanksWithStowage = new Set(existingStowageItems.map((s) => s.tankId));
    const emptyTanks = tanks.filter((t) => !tanksWithStowage.has(t.tankId));

    // Create new stowage items for empty tanks
    const newStowageItems: Stowage[] = [];
    if (emptyTanks.length > 0) {
        let referenceCargoOperation: RotationCargoOperation;

        if (existingStowageItems.length > 0) {
            // Use existing stowage item's cargo operation
            referenceCargoOperation = existingStowageItems[0].cargoOperation;
        } else {
            // No existing stowage items - find a compatible loading operation for this cargo
            const compatibleCargoOperation = workStowagePlan.cargoOperations?.find(
                (op) => op.cargoGuid === cargo.cargoGuid && op.operationType === 'Loading' && op.berthCall.berthCallGuid === berthCall.berthCallGuid
            );

            if (!compatibleCargoOperation) {
                // No compatible cargo operation found - cannot proceed
                return stowagePlanChanges;
            }
            referenceCargoOperation = compatibleCargoOperation;
        }

        emptyTanks.forEach((tank) => {
            const newStowageItem: Stowage = {
                // TODO: CRYPTO-IMPORT - crypto.randomUUID() is used without importing crypto module. Should add 'import { randomUUID } from 'crypto';' or use a proper UUID generator.
                stowageGuid: crypto.randomUUID(),
                tankId: tank.tankId,
                cargoOperationGuid: referenceCargoOperation.cargoOperationGuid,
                cargoOperation: referenceCargoOperation,
                tank: tank,
                weight: new Decimal(0), // Start with zero weight, will be calculated in leveling
            };

            newStowageItems.push(newStowageItem);
            workStowagePlan.stowageItems.push(newStowageItem);

            // Create a change for the new stowage item
            stowagePlanChanges.push(
                createStowagePlanChange(workStowagePlan.stowagePlanGuid, newStowageItem.stowageGuid, StowageChangeDataType.StowageItem, {
                    tankId: newStowageItem.tankId,
                    cargoOperationGuid: newStowageItem.cargoOperationGuid,
                    weight: newStowageItem.weight,
                })
            );
        });
    }

    // Combine existing and new stowage items for processing
    const stowageItems = [...existingStowageItems, ...newStowageItems];

    // If we have no stowage items after attempting to create them, cannot proceed
    if (stowageItems.length === 0) {
        return stowagePlanChanges;
    }

    const tanksStowageWeight = stowageItems.reduce((acc, s) => acc.plus(s.weight), new Decimal(0));
    const adjustedWeights = stowageItems.reduce(
        (acc, s) => acc.plus(new Decimal(s.tank.volume).times(getCorrectedLitreWeight(getCargoTemperature(workStowagePlan, cargo, s.tank), cargo))),
        new Decimal(0)
    );
    const oldTotalWeight = stowageItems.reduce((acc, s) => acc.plus(s.weight), new Decimal(0));

    if (adjustedWeights.gt(0)) {
        const fillRatio = tanksStowageWeight.dividedBy(adjustedWeights);
        stowageItems.forEach((s) => {
            const stowageTank = s.tank;
            const stowageCargoOperation = s.cargoOperation;
            const volumeToBeStowedInTank = new Decimal(stowageTank.volume).times(fillRatio);
            s.weight = calculateWeight(
                volumeToBeStowedInTank,
                stowageCargoOperation.cargo,
                getCargoTemperature(workStowagePlan, stowageCargoOperation.cargo, s.tank)
            ).toDecimalPlaces(3, Decimal.ROUND_DOWN);
            stowagePlanChanges.push(
                createStowagePlanChange(workStowagePlan.stowagePlanGuid, s.stowageGuid, StowageChangeDataType.StowageItem, { weight: s.weight })
            );
            if (logLevelTankUtils) console.log(`Stowing ${s.weight.toNumber()} in tank ${s.tank.tankId}.`);
        });

        const newTotalWeight = stowageItems.reduce((acc, s) => acc.plus(s.weight), new Decimal(0));

        if (oldTotalWeight.gt(newTotalWeight)) {
            if (logLevelTankUtils)
                console.log(
                    `Found weight discrepancy. Old total weight: ${oldTotalWeight.toNumber()}, new total weight: ${newTotalWeight.toNumber()}.`
                );
            const difference = oldTotalWeight.minus(newTotalWeight);
            if (logLevelTankUtils) console.log('Adding difference to first tank: ', difference.toNumber());
            // TODO: ARRAY-INDEX-SAFETY - Array access without bounds checking. stowageItems[0] may be undefined if array is empty, despite earlier length check.
            stowageItems[0].weight = stowageItems[0].weight.plus(difference);
            stowagePlanChanges.push(
                createStowagePlanChange(workStowagePlan.stowagePlanGuid, stowageItems[0].stowageGuid, StowageChangeDataType.StowageItem, {
                    weight: stowageItems[0].weight,
                })
            );
        }
    } //TODO: VALIDATION - update check
    if (stowageItems.length > 0) {
        updateManifest(workStowagePlan, vessel);
        const unstowStowagePlanChanges = updateUnstowOperationsForCargo(workStowagePlan, stowageItems[0].cargoOperation);
        stowagePlanChanges = stowagePlanChanges.concat(unstowStowagePlanChanges);
    }

    return stowagePlanChanges;
};
